import React, { useRef, useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useMatch } from "react-router";

import clsx from "clsx";
import {
  Grow,
  Paper,
  Avatar,
  Button,
  Popper,
  AppBar,
  Switch,
  Toolbar,
  Tooltip,
  Divider,
  MenuItem,
  MenuList,
  Typography,
  ClickAwayListener,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import CreditCardIcon from "@mui/icons-material/CreditCard";
import TableChartIcon from "@mui/icons-material/TableChart";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { PriceChange as PriceChangeIcon } from "@mui/icons-material";

import BillingModal from "./BillingModal";
import LatePatientModal from "./LatePatientModal";
import ChangePasswordModal from "./ChangePasswordModal";

import logo from "assets/images/evoke_logo.png";
import cgmLogo from "assets/images/cgm_logo.png";

import "./index.css";
import {
  getMFAStatus,
  updateMFAStatus,
  validateOTPForEnable,
} from "app/services/dashboard/mfa.service";
import { OTPModal } from "./OTPModal";
import LateRPMPatient from "./late_rpm/LateRPMPatient";
import RPMBillingModal from "./RPMBillingModal/RPMBillingModal";
import LoadingModal from "components/common/loading/LoadingModal";

import { useNavigate } from "react-router-dom";

import {
  USER_ROLES,
  OTP_BLOCKED_ERROR_CODES,
  TOAST_AUTO_HIDE_TIME,
} from "static/constants";
import { showErrorToast, showSuccessToast } from "utils/toast";
import { generateAvatar } from "app/utils/avatar";
import SwitchRoleMenu from "./switchRole/SwitchRoleMenu";
import useCheckRPMSaaSAccountUser from "hooks/useCheckRPMSaaSAccountUser";
import { handleLogout } from "utils/auth";
import Notification from "./Notification";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  appBar: {
    zIndex: `${theme.zIndex.drawer + 1} !important`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    borderBottom: "1px solid #E0E0E0",
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
  },
  hide: {
    display: "none",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
  },
  profileMenu: {
    top: "50px !important",
  },
  userDropdownItem: {
    paddingTop: "15px !important",
    paddingBottom: "15px !important",
  },
  userDropdownSwitchItem: {
    paddingTop: "2px !important",
    paddingBottom: "2px !important",
  },
}));

export default function Header({ open, view, role }) {
  const classes = useStyles();

  const isRPMView = window.location.pathname === "/rpm";

  const { reportList, title } = useSelector((state) => state.transmission);
  const isReportPage = Boolean(useMatch("/patient/report/:reportId"));
  const { currentUser } = useSelector((state) => state.auth);
  const { accounts } = useSelector((state) => state.account);

  const navigate = useNavigate();

  const anchorRef = useRef(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [mfaStatus, setMfaStatus] = useState(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [isDisableMFA, setIsDisableMFA] = useState(false);
  const [isMfaOtpBlocked, setIsMfaOtpBlocked] = useState(false);
  const [state, setState] = useState({
    billingModalOpen: false,
    passwordModalOpen: false,
    latePatientModalOpen: false,
    lateRpmModalOpen: false,
    anchorElProfileMenu: null,
    rpmBillingModalOpen: false,
  });

  const hasRPMDevices =
    JSON.parse(localStorage.getItem("hasRPMDevices")) ?? false;

  useEffect(() => {
    getUserMFAStatus();
  }, []);

  const handleUserLogout = async (forceLogout = false) => {
    try {
      setIsLoggingOut(true);
      await handleLogout(forceLogout);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const getUserMFAStatus = async () => {
    try {
      const response = await getMFAStatus();
      if (response && response.data) {
        setMfaStatus(response.data.mfaStatus);
        setIsMfaOtpBlocked(!!response.data.isOtpBlocked);
      }
    } catch (error) {
      showErrorToast(error.message);
    }
  };

  const isRPMSaaSUser = useCheckRPMSaaSAccountUser();

  const toggleRpmBillingModal = () => {
    const { rpmBillingModalOpen } = state;
    setState({
      rpmBillingModalOpen: !rpmBillingModalOpen,
    });
  };

  const toggleBillingModal = () => {
    const { billingModalOpen } = state;
    setState({
      billingModalOpen: !billingModalOpen,
    });
  };

  const toggleLateRpmModal = () => {
    const { lateRpmModalOpen } = state;
    setState({
      lateRpmModalOpen: !lateRpmModalOpen,
    });
  };

  const toggleLatePatientModal = () => {
    const { latePatientModalOpen } = state;
    setState({
      latePatientModalOpen: !latePatientModalOpen,
    });
  };

  const togglePasswordModal = () => {
    const { passwordModalOpen } = state;
    setState({
      passwordModalOpen: !passwordModalOpen,
      anchorElProfileMenu: null,
    });
  };

  const toggleOtpModal = () => {
    setIsOtpModalOpen(!isOtpModalOpen);
  };

  const toggleMFAStatus = async () => {
    try {
      const mfaStatusValue = !mfaStatus;
      setMfaStatus(mfaStatusValue);
      toggleOtpModal();

      if (!mfaStatusValue) {
        setIsDisableMFA(!mfaStatusValue);
      }
    } catch (error) {
      // revert switch status to initial state
      setMfaStatus(mfaStatus);
      showErrorToast(error.message);
    }
  };

  const handleOtpRequestBlocked = useCallback(() => {
    setIsMfaOtpBlocked(true);
    setMfaStatus((prevMfaStatus) => !prevMfaStatus);
    setIsOtpModalOpen(false);
  }, []);

  const handleOTPValidation = async (otp) => {
    try {
      const isOTPValid = await validateOTPForEnable({ otp });
      const mfaStatusValue = !mfaStatus;

      if (isOTPValid) {
        const updateMFAStatusResponse = await updateMFAStatus();
        if (updateMFAStatusResponse) {
          showSuccessToast(updateMFAStatusResponse.message);
          toggleOtpModal();
          if (!mfaStatusValue) {
            setTimeout(() => handleUserLogout(true), TOAST_AUTO_HIDE_TIME);
          }
        }
      }
    } catch (error) {
      showErrorToast(error.message);
      if (OTP_BLOCKED_ERROR_CODES.includes(error?.data?.code)) {
        handleOtpRequestBlocked();
      }
    }
  };

  const handleToggleProfileMenu = () => {
    setProfileMenuOpen((prevOpen) => !prevOpen);
  };

  const handleCloseProfileMenu = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setProfileMenuOpen(false);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      className={clsx(classes.appBar, {
        [classes.appBarShift]: open,
      })}
    >
      <Toolbar>
        <img
          src={isRPMSaaSUser ? cgmLogo : logo}
          alt="logo"
          className="toolbar__logo"
        />

        {(isReportPage || title !== "") && (
          <>
            <Divider orientation="vertical" className="header__divider" />

            <div className="header__title">
              {isReportPage && reportList ? reportList[0]?.name : title}
            </div>
          </>
        )}

        <div className="header__profile">
          <Notification />

          {!isRPMSaaSUser && (
            <>
              {isRPMView &&
                currentUser?.clinii_user_id &&
                ([USER_ROLES.ADMIN, USER_ROLES.USER].includes(role) ||
                  (role === USER_ROLES.CLINIC && hasRPMDevices)) && (
                  <Button
                    onClick={() => navigate("/patient/rpm")}
                    variant="text"
                    color="secondary"
                    size="small"
                    className="mr-2"
                  >
                    Switch to Old RPM Interface
                  </Button>
                )}

              {[
                USER_ROLES.ADMIN,
                USER_ROLES.ASSISTANT,
                USER_ROLES.USER,
                USER_ROLES.CLINIC_ADMIN,
              ].includes(currentUser?.role) && (
                <>
                  {(currentUser?.role === USER_ROLES.CLINIC_ADMIN &&
                    hasRPMDevices) ||
                  [USER_ROLES.ADMIN, USER_ROLES.USER].includes(
                    currentUser?.role
                  ) ? (
                    <>
                      <span
                        className="header__profile-icon"
                        onClick={toggleRpmBillingModal}
                      >
                        <Tooltip title="RPM Billing Invoice" placement="bottom">
                          <PriceChangeIcon />
                        </Tooltip>
                      </span>
                      <span
                        className="header__profile-icon"
                        onClick={toggleLateRpmModal}
                      >
                        <Tooltip title="Late RPM" placement="bottom">
                          <FavoriteIcon />
                        </Tooltip>
                      </span>
                    </>
                  ) : null}

                  <span
                    className="header__profile-icon"
                    onClick={toggleLatePatientModal}
                  >
                    <Tooltip title="Late Patients" placement="bottom">
                      <TableChartIcon />
                    </Tooltip>
                  </span>
                </>
              )}

              <span
                className="header__profile-icon"
                onClick={toggleBillingModal}
              >
                <Tooltip title="Billing History" placement="bottom">
                  <CreditCardIcon />
                </Tooltip>
              </span>

              {currentUser?.secondary_roles?.length > 0 && <SwitchRoleMenu />}
            </>
          )}

          <Button
            ref={anchorRef}
            onClick={handleToggleProfileMenu}
            aria-haspopup="true"
            aria-label="toggle-profile-menu"
            aria-controls={profileMenuOpen ? "menu-list-grow" : undefined}
            startIcon={
              <Avatar
                sx={{
                  backgroundColor: "#eaf2ff",
                  color: "#175ec8",
                  height: "30px",
                  width: "30px",
                }}
                alt={currentUser?.name}
              >
                <div style={{ fontSize: "12px" }}>
                  {generateAvatar(currentUser?.name)}
                </div>
              </Avatar>
            }
            endIcon={<ArrowDropDownIcon />}
          >
            {currentUser?.name}
          </Button>
          <Popper
            open={profileMenuOpen}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === "bottom" ? "center top" : "center bottom",
                  maxWidth: "300px",
                  width: "100%",
                  marginRight: "122px",
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleCloseProfileMenu}>
                    <MenuList
                      autoFocusItem={profileMenuOpen}
                      id="menu-list-grow"
                      className="user-dropdown"
                    >
                      <MenuItem
                        key={3}
                        onClick={(event) => {
                          togglePasswordModal();
                          handleCloseProfileMenu(event);
                        }}
                        className={classes.userDropdownItem}
                      >
                        <Typography textAlign="center">
                          Change Password
                        </Typography>
                      </MenuItem>
                      {/* <MenuItem key={2}>
                        <FormControlLabel
                          style={{ margin: 0 }}
                          control={
                            <Switch
                              onChange={() => {
                                toggleAppTheme();
                                handleCloseProfileMenu();
                              }}
                              checked={darkTheme}
                              name={"darkMode"}
                            />
                          }
                          label={"Dark Mode"}
                          labelPlacement="start"
                        />
                      </MenuItem> */}
                      <MenuItem
                        key={4}
                        className={classes.userDropdownSwitchItem}
                      >
                        <Typography textAlign="center">Enable MFA</Typography>
                        <Tooltip
                          title={
                            isMfaOtpBlocked
                              ? "Too many OTP requests. Please contact support to enable MFA."
                              : ""
                          }
                        >
                          <span>
                            <Switch
                              onChange={() => {
                                toggleMFAStatus();
                              }}
                              checked={mfaStatus}
                              disabled={isMfaOtpBlocked}
                              name="MFAStatus"
                            />
                          </span>
                        </Tooltip>
                      </MenuItem>
                      <Divider />
                      <MenuItem
                        key={5}
                        onClick={(event) => {
                          handleUserLogout();
                          anchorRef.current = null;

                          handleCloseProfileMenu(event);
                        }}
                        className={classes.userDropdownItem}
                      >
                        <Typography textAlign="center">Log Out</Typography>
                      </MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </div>
        {state.rpmBillingModalOpen && (
          <RPMBillingModal
            role={role}
            open={state.rpmBillingModalOpen}
            onClose={toggleRpmBillingModal}
            accounts={accounts}
          />
        )}

        {state.billingModalOpen && (
          <BillingModal
            billingModalOpen={state.billingModalOpen}
            closeBillingModal={toggleBillingModal}
            view={view}
            role={role}
            accounts={accounts}
          />
        )}

        {state.lateRpmModalOpen && (
          <LateRPMPatient
            lateRpmModalOpen={state.lateRpmModalOpen}
            closeLateRpmModal={toggleLateRpmModal}
            view={view}
            role={role}
            accounts={accounts}
          />
        )}

        {state.latePatientModalOpen && (
          <LatePatientModal
            latePatientModalOpen={state.latePatientModalOpen}
            closeLatePatientModal={toggleLatePatientModal}
            view={view}
            role={role}
            accounts={accounts}
          />
        )}

        {state.passwordModalOpen && (
          <ChangePasswordModal
            passwordModalOpen={state.passwordModalOpen}
            closePasswordModal={togglePasswordModal}
            view={view}
          />
        )}

        {isOtpModalOpen && (
          <OTPModal
            isModalOpen={isOtpModalOpen}
            toggleOtpModal={toggleOtpModal}
            handleOTPValidation={handleOTPValidation}
            isEmailVerification={true}
            setMfaStatus={setMfaStatus}
            mfaStatus={mfaStatus}
            isDisableMFA={isDisableMFA}
            onOtpRequestBlocked={handleOtpRequestBlocked}
          />
        )}

        {isLoggingOut && <LoadingModal loadingText="Logging out..." />}
      </Toolbar>
    </AppBar>
  );
}
